import apiClient from "./client";

import type { MigrationLogScreenshot } from "@/api/config-migration";

/**
 * Cliente da conversa do agente de configuração (spec 2026-10-09, §6.4; spec
 * 2026-10-12, §6.7).
 *
 * Cada conversa é um run (`config_migration_run` com o `node` de quem a abriu), e o
 * id da conversa é o `run_id`. O log de eventos é a `config_migration_log`: o que
 * chega aqui é a linha do log com `event_type`, e a tela inteira sai dela. O formato
 * do evento é o mesmo nos Nós 03 e 04; de um nó para o outro muda só o segmento da
 * rota, que `createConversationClient(node)` recebe.
 *
 * As chamadas REST vão pelo `apiClient`, que manda o `Accept-Language` da tela.
 * A conversa usa esse idioma como indicação na entrada; a resposta segue o último
 * idioma do consultor. Só o stream usa `fetch` + `getReader()`, o padrão da casa
 * para SSE com cookie.
 */

export const MAX_CONVERSATION_MESSAGE_LENGTH = 4000;

/** Falhas locais do leitor; a causa preserva a exceção para diagnóstico. */
export class ConversationStreamError extends Error {
  readonly name = "ConversationStreamError";
  constructor(
    readonly code: "stream_protocol_error" | "stream_callback_error",
    readonly cause: unknown,
  ) {
    super(code);
  }
}

/** Normaliza recusas REST e SSE sem expor detalhes técnicos ao usuário. */
export function conversationFailure(error: unknown): { code: string; retryable: boolean; closing: boolean } {
  if (error instanceof ConversationStreamError) return { code: error.code, retryable: false, closing: false };
  const value = error as { response?: { status?: number; data?: { detail?: unknown } }; status_code?: number; detail?: unknown } | null;
  const status = value?.response?.status ?? value?.status_code;
  const detail = value?.response?.data?.detail ?? value?.detail;
  const code = status === 503 ? "service_unavailable"
    : status === 502 ? "gateway_failed"
    : typeof detail === "string" ? detail
    : !status ? "network_failed"
    : status === 403 ? "access_denied"
      : status === 404 ? "conversation_not_found"
        : status === 422 ? "invalid_message"
          : status && status < 500 ? "request_refused" : "connection_failed";
  const closing = status === 409 && code === "session_closing";
  return { code, closing, retryable: closing || (code !== "conversation_closed" && (!status || status >= 500)) };
}

export type ConversationStatus =
  | "running"
  | "waiting"
  | "paused"
  | "completed"
  | "failed"
  | "interrupted";

/** Status de conversa que acabou: não há mais nada a ouvir no stream. */
export const ENDED_STATUSES: ReadonlySet<ConversationStatus> = new Set<ConversationStatus>([
  "completed",
  "failed",
  "interrupted",
]);

/** Os tipos de evento da spec §6.2, sem tirar nem pôr. */
export type ConversationEventType =
  | "conversation_started"
  | "session_opened"
  | "session_reattached"
  | "session_closed"
  | "conversation_ended"
  | "login_checked"
  | "target_opened"
  | "screen_read"
  | "screenshot_taken"
  | "turn_started"
  | "turn_completed"
  | "context_compacted"
  | "consultant_message"
  | "agent_message"
  | "agent_question"
  | "refusal"
  | "model_call"
  | "tool_call"
  | "tool_result"
  | "approval_requested"
  | "approval_decided"
  | "save_approval_revoked"
  | "row_plan_approved"
  | "row_started"
  | "row_completed"
  | "script_recorded"
  | "script_deviation"
  | "error";

export type ConversationActor = "agent" | "consultant" | "system";

/** Quadro transitório `agent_status` do stream: o que o agente faz agora. Não vai para o log. */
export type AgentActivity = "thinking" | "acting" | "waiting";

export type QuestionFormat = "options" | "yes_no" | "mapping" | "free_text";
/** `discard`: aprovação exigida antes de descartar alterações não salvas. */
export type ApprovalKind = "row_plan" | "save" | "script" | "discard" | "continue_rows";
export type InterruptionCause = "stop" | "end" | "idle" | "restart";
export type ApprovalDecisionValue = "approve" | "reject" | "expired";
export type RowOutcome = "created" | "already_existed" | "failed" | "skipped";
export type RowPath = "script" | "conversation";

/**
 * Uma linha do log de eventos, como a API serializa o `StoredEvent`.
 * O quadro do SSE traz também `tenant_id`, que a tela
 * ignora (o gateway já o conferiu).
 */
export interface ConversationEvent {
  /** Id da linha no banco (`config_migration_log.id`): é por ele que a rota do print acha a imagem. */
  id: number;
  run_id: string;
  item_id: string | null;
  seq: number;
  event_type: ConversationEventType;
  turn_id: string | null;
  actor: ConversationActor;
  actor_user_id: string | null;
  level: "info" | "warn" | "error";
  /** Frase curta em inglês, para o log técnico. O conteúdo vai no `payload`. */
  message: string;
  payload: Record<string, unknown>;
  created_at: string;
}

// Payloads que a tela lê, espelho dos modelos Pydantic dos eventos.

export interface ConsultantMessagePayload {
  text: string;
  client_message_id: string;
  reply_to?: string | null;
  accept_language?: string | null;
}

export interface AgentMessagePayload {
  text: string;
  /** `null` quando o agente ainda não declarou o idioma da resposta. A tela não o lê. */
  language: string | null;
}

export interface MappingQuestionField {
  ref: string;
  label: string;
  choices: string[];
}

export interface AgentQuestionPayload {
  question_id: string;
  text: string;
  language: string;
  format: QuestionFormat;
  choices: string[];
  mapping_fields?: MappingQuestionField[];
  /** Nome da constante que a resposta define (`const:<nome>`); a tela não usa. */
  constant?: string | null;
}

export interface ModelCallPayload {
  /** Ausência em logs históricos não comprova leitura. */
  consumed_message_ids?: string[];
}

export interface ErrorPayload {
  error_class:
    | "invalid_output"
    | "element_not_found"
    | "sap_business"
    | "technical"
    | "blocked"
    | "refused_by_guard";
  message: string;
  code?: string | null;
}

export interface RefusalPayload {
  /** Código i18n completo, ex.: `refusal.off_topic`. A tela traduz (D8). */
  code: string;
}

/** Dados integrais vinculados ao hash da aprovação; a prévia visual pode ser menor. */
export interface RowPlanApprovalData {
  table: string;
  filters: Record<string, string>;
  rows: Array<{ row_index: number; values: Record<string, string> }>;
}

export interface SaveApprovalData {
  screen_digest: string;
  row_index: number | null;
  fields: Array<{ ref: string; label: string; value: string }>;
}

export interface ScriptApprovalData {
  row_index: number;
  steps: Array<{ action: string; target: string | null; value: string | null; key: string | null }>;
  unused_columns: string[];
}

interface ApprovalRequestedBase {
  approval_id: string;
  /**
   * Texto do modelo, no idioma da conversa. Nas aprovações que o código pede é um código
   * (`approval.confirm_first_scripted_row`, `approval.continue_rows`) ou, no `discard`, uma
   * frase fixa em inglês: a tela traduz as duas (D8).
   */
  summary: string;
  data_hash: string;
}

export type ApprovalRequestedPayload = ApprovalRequestedBase & ({
  kind: Exclude<ApprovalKind, "continue_rows">;
  data: Record<string, unknown>;
} | {
  kind: "continue_rows";
  data: Record<string, unknown> & { reason: string; remaining_rows: number };
});

export interface ApprovalDecidedPayload {
  approval_id: string;
  decision: ApprovalDecisionValue;
  comment?: string | null;
}

/** Mesmas chaves que a rota de print existente lê (S-081). */
export interface SaveApprovalRevokedPayload {
  approval_id: string;
  data_hash: string;
  reason: "interrupted";
}

export interface ScreenshotTakenPayload {
  screenshot: MigrationLogScreenshot;
}

export interface RowPlanApprovedPayload {
  /** A API antiga omitia a chave; o modelo atual a emite com default vazio. */
  unused_columns?: string[];
  table: string;
  filters: Record<string, string>;
  rows: Array<Record<string, unknown>>;
}

/** `row_index` começa em 0. */
export interface RowStartedPayload {
  row_index: number;
}

export interface RowCompletedPayload {
  row_index: number;
  outcome: RowOutcome;
  sap_message?: string | null;
  path: RowPath;
}

export interface ScriptRecordedPayload {
  code: string;
  version: number;
}

export interface SessionClosedPayload {
  reason: "idle" | "ended" | "error" | "restart";
  exit_ok?: boolean;
  discarded_fields?: string[];
}

export interface LoginCheckedPayload {
  path: "already_authenticated" | "script" | "llm" | "failed";
  detail?: string;
}

/** A tela do item abriu (`entry_url` é a URL de entrada: link direto ou home do launchpad). */
export interface TargetOpenedPayload {
  entry_url: string;
  editable: boolean;
  blocked_reason?: string | null;
  screen_id?: string | null;
  digest?: string | null;
}

export interface TurnCompletedPayload {
  status: "completed" | "interrupted" | "failed";
  cause?: InterruptionCause | null;
}

/**
 * `conversation_started`: `details.script_mode` é como a conversa abriu. `script` é o
 * sim do consultor ao roteiro pronto (sem conversa até o roteiro desviar); `auto` e
 * `llm` são conversas comuns. Logs antigos não trazem a chave.
 */
export interface ConversationStartedPayload {
  details?: { script_mode?: "auto" | "script" | "llm" } & Record<string, unknown>;
}

export interface ConversationEndedPayload {
  status: "completed" | "interrupted" | "failed";
}

export interface ConversationView {
  run_id: string;
  status: ConversationStatus;
  /**
   * Estado derivado no servidor (`state_to_dict`), só com `after_seq=0`; no
   * polling (`after_seq>0`) vem `null`. A tela não o lê: recalcula o dela a
   * partir dos eventos, com o mesmo log como fonte.
   */
  state: Record<string, unknown> | null;
  events: ConversationEvent[];
  /**
   * Quem apertou o play (D14): a tela compara com quem está vendo para decidir se conduz ou só
   * acompanha. O servidor só o devolve na leitura completa (`after_seq=0`); no polling
   * (`after_seq>0`) vem `null`, e a tela guarda o da primeira leitura. Também é `null`/ausente
   * em run legada, sem dono.
   */
  requested_by?: string | null;
  requested_by_name?: string | null;
}

/**
 * A conversa aberta do projeto, como `GET ?status=open` devolve (`OpenConversationView`).
 * `item_id` e `activity_code` vêm `null` na janela entre criar o run e o item apontar
 * para ele.
 */
export interface ConversationSummary {
  run_id: string;
  status: ConversationStatus;
  item_id: string | null;
  activity_code: string | null;
  started_at: string;
}

export interface OpenedConversation {
  runId: string;
  /**
   * Item da conversa. `null` quando o ▶ religou a uma conversa cujo item ainda não aponta
   * para o run: ela pode ser de outro item, e o título fica genérico.
   */
  activityCode: string | null;
  /** `true` quando o ▶ religou a uma conversa que já estava aberta. */
  resumed: boolean;
}

export interface MessageBody {
  client_message_id: string;
  text: string;
  reply_to?: string | null;
}

/**
 * Roteamento da mensagem (202). `duplicate`: o mesmo `client_message_id` já tinha
 * entrado — o reenvio depois de uma falha de rede não vira mensagem dupla.
 * Recusa (conversa fechada) chega como erro HTTP, com o código no `detail`.
 */
export interface MessageRouting {
  outcome: "started" | "steered" | "duplicate";
  turn_id: string | null;
}

export interface ConversationStreamHandlers {
  /** O servidor aceitou o stream (HTTP 2xx com corpo). */
  onOpen?: () => void;
  onEvent: (event: ConversationEvent) => void;
  onAgentStatus?: (activity: AgentActivity) => void;
  /** O servidor fechou o stream sem erro (ex.: depois de `conversation_ended`). */
  onClose?: () => void;
  /**
   * Transporte/HTTP/gateway ou ConversationStreamError de protocolo/callback.
   * Chamado após onClose somente se esse callback lançar uma exceção.
   */
  onError?: (error: unknown) => void;
}

const AGENT_ACTIVITIES: ReadonlySet<string> = new Set(["thinking", "acting", "waiting"]);

function isConversationEvent(data: Record<string, unknown>, runId: string): data is Record<string, unknown> & ConversationEvent {
  return Number.isInteger(data.id) && Number.isInteger(data.seq) && Number(data.seq) > 0
    && data.run_id === runId && typeof data.tenant_id === "string" && data.tenant_id.length > 0
    && typeof data.event_type === "string" && data.event_type.length > 0
    && (typeof data.item_id === "string" || data.item_id === null)
    && (typeof data.turn_id === "string" || data.turn_id === null)
    && (typeof data.actor_user_id === "string" || data.actor_user_id === null)
    && typeof data.actor === "string" && ["agent", "consultant", "system"].includes(data.actor)
    && typeof data.level === "string" && ["info", "warn", "error"].includes(data.level)
    && typeof data.message === "string" && typeof data.created_at === "string"
    && typeof data.payload === "object" && data.payload !== null && !Array.isArray(data.payload);
}

interface SseFrame {
  event: string;
  data: string;
}

/**
 * Separa quadros completos: LF é o formato do gateway; CRLF também é aceito.
 * Normaliza o buffer acumulado porque o separador pode cruzar chunks.
 * Comentários e o quadro final sem delimitador não são eventos.
 */
function takeFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const blocks = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = blocks.pop() ?? "";
  const frames: SseFrame[] = [];
  for (const block of blocks) {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith(":")) continue;
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trim());
    }
    if (data.length > 0) frames.push({ event, data: data.join("\n") });
  }
  return { frames, rest };
}

/**
 * Abre o SSE da conversa a partir de `afterSeq`: o servidor reenvia o que faltou,
 * inclusive a pendência, e segue ao vivo.
 *
 * O cliente controla a reconexão (o hook religa pelo último `seq` visto), então o
 * `after_seq` vai na query, que é o contrato da rota; o `Last-Event-ID` vai junto
 * por ser o cabeçalho padrão do SSE para o mesmo fim.
 *
 * Quadros: o evento do log vem com `event: <event_type>` e `id: <seq>`;
 * `agent_status` é transitório; `error` sem `seq` é o gateway recusando depois de
 * abrir o stream, com HTTP 200. Quem decide se um quadro é evento é a forma do `data`
 * (tem `seq` e `event_type`), e isso vem antes do nome: o evento gravado de tipo
 * `error` também chega como `event: error`, e tipo novo no servidor não some aqui.
 * Termina em `onClose` ou `onError`, e em nenhum se for abortado.
 * Se `onClose` lançar, `onError` recebe a falha específica desse callback.
 *
 * `streamUrl` é a rota do stream do run no segmento do nó; quem a monta é o cliente
 * (`createConversationClient`), e `runId` serve para recusar quadro de outro run.
 */
function streamConversation(
  streamUrl: string,
  runId: string,
  afterSeq: number,
  handlers: ConversationStreamHandlers,
): AbortController {
  const controller = new AbortController();

  void (async () => {
    let finished = false;
    const finish = (error?: unknown) => {
      if (finished || controller.signal.aborted) return;
      finished = true;
      if (error === undefined) {
        try {
          handlers.onClose?.();
        } catch (callbackError) {
          handlers.onError?.(new ConversationStreamError("stream_callback_error", callbackError));
        }
      } else handlers.onError?.(error);
    };

    const invoke = (callback: () => void): boolean => {
      if (finished || controller.signal.aborted) return false;
      try {
        callback();
        return !finished && !controller.signal.aborted;
      } catch (error) {
        finish(new ConversationStreamError("stream_callback_error", error));
        return false;
      }
    };

    let response: Response;
    try {
      response = await fetch(
        `${streamUrl}?after_seq=${afterSeq}`,
        {
          method: "GET",
          headers: { Accept: "text/event-stream", "Last-Event-ID": String(afterSeq) },
          credentials: "include",
          signal: controller.signal,
        },
      );
    } catch (error) {
      if ((error as Error).name !== "AbortError") finish(error);
      return;
    }
    if (controller.signal.aborted) return;
    if (!response.ok || !response.body) {
      const body: unknown = await response.json().catch(() => null);
      const detail = typeof body === "object" && body !== null && "detail" in body ? body.detail : undefined;
      finish({ status_code: response.status, detail });
      return;
    }
    if (!invoke(() => handlers.onOpen?.())) {
      await response.body.cancel().catch(() => {});
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      while (!finished && !controller.signal.aborted) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch (error) {
          if ((error as Error).name !== "AbortError") finish(error);
          return;
        }
        if (chunk.done) {
          finish();
          return;
        }
        const parsed = takeFrames(buffer + decoder.decode(chunk.value, { stream: true }));
        buffer = parsed.rest;
        for (const frame of parsed.frames) {
          let data: unknown;
          try {
            data = JSON.parse(frame.data);
          } catch (error) {
            finish(new ConversationStreamError("stream_protocol_error", error));
            return;
          }
          if (typeof data !== "object" || data === null || Array.isArray(data)) {
            finish(new ConversationStreamError("stream_protocol_error", new Error("invalid SSE envelope")));
            return;
          }
          const envelope = data as Record<string, unknown>;
          // O gateway esvazia quadros de outro tenant; não são falhas de formato.
          if (Object.keys(envelope).length === 0) continue;
          // Evento persistido de tipo error é entregue ao redutor antes do erro do gateway.
          if ("seq" in envelope || "event_type" in envelope) {
            if (!isConversationEvent(envelope, runId)) {
              finish(new ConversationStreamError("stream_protocol_error", new Error("invalid conversation event")));
              return;
            }
            if (!invoke(() => handlers.onEvent(envelope))) return;
          } else if (frame.event === "error") {
            finish(data);
            return;
          } else if (frame.event === "agent_status") {
            if (typeof envelope.tenant_id !== "string" || !envelope.tenant_id) {
              finish(new ConversationStreamError("stream_protocol_error", new Error("missing status tenant")));
              return;
            }
            const status = envelope.status;
            if (typeof status === "string" && AGENT_ACTIVITIES.has(status)) {
              if (!invoke(() => handlers.onAgentStatus?.(status as AgentActivity))) return;
            }
          } else {
            finish(new ConversationStreamError("stream_protocol_error", new Error("invalid SSE frame")));
            return;
          }
        }
      }
    } finally {
      // Cancelamento é limpeza após o resultado; não reclassifica o erro original.
      await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  })();

  return controller;
}

/** Segmento da rota de cada nó que conversa: `org-structure` (Nó 02), `sscui` (Nó 03) e `module-config` (Nó 04). */
export type ConversationNode = "org-structure" | "sscui" | "module-config";

/** Dados da unidade que o Nó 02 usa na abertura (a unidade e os ancestrais, no formato do wire). */
export type StartContext = Record<string, unknown>;

/**
 * As rotas da conversa de um nó. Os métodos são funções soltas, sem `this`: o cliente
 * pode ser desestruturado e passado adiante.
 */
export interface ConversationClient {
  readonly node: ConversationNode;
  /** Abre a conversa (201). Recusa com o código no `detail`; as do Nó 03 estão em `sscui-conversation.ts`. */
  start(projectId: string, activityCode: string, context?: StartContext): Promise<{ run_id: string }>;
  /** A conversa aberta do projeto (`running`, `waiting` ou `paused`): há no máximo uma (D9). */
  getOpen(projectId: string): Promise<ConversationSummary | null>;
  fetchView(projectId: string, runId: string, afterSeq?: number): Promise<ConversationView>;
  /** O SSE da conversa a partir de `afterSeq` (ver `streamConversation`). */
  openStream(
    projectId: string,
    runId: string,
    afterSeq: number,
    handlers: ConversationStreamHandlers,
  ): AbortController;
  sendMessage(projectId: string, runId: string, body: MessageBody): Promise<MessageRouting>;
  /** Parar: cancela o turno depois da ação em andamento; o navegador continua aberto. */
  interrupt(projectId: string, runId: string): Promise<void>;
  /** Encerrar: sai da transação, fecha o navegador e fecha o run. */
  end(projectId: string, runId: string): Promise<void>;
  /**
   * O que o ▶ faz: religa à conversa aberta do projeto, se houver, ou abre uma nova.
   *
   * Perguntar antes de abrir é o que evita o 409 da segunda conversa (D9) e devolve
   * à pessoa o caminho para a conversa que ela deixou aberta — mesmo que seja de
   * outro item (`resumed` com outro `activity_code`). Sem `activity_code` (a conversa
   * acabou de abrir e o item ainda não aponta para o run), religa ao run com o código
   * `null`: a conversa pode ser de outro item, e mostrar o código clicado enganaria.
   */
  openOrResume(projectId: string, activityCode: string, context?: StartContext): Promise<OpenedConversation>;
}

/** O cliente das rotas `/api/projects/{id}/config-migration/{node}/conversations…`. */
export function createConversationClient(node: ConversationNode): ConversationClient {
  const conversationsPath = (projectId: string) =>
    `/api/projects/${projectId}/config-migration/${node}/conversations`;
  const runPath = (projectId: string, runId: string) => `${conversationsPath(projectId)}/${runId}`;

  const start = async (
    projectId: string,
    activityCode: string,
    context?: StartContext,
  ): Promise<{ run_id: string }> => {
    const response = await apiClient.post<{ run_id: string }>(conversationsPath(projectId), {
      activity_code: activityCode,
      ...(context ? { context } : {}),
    });
    return response.data;
  };

  const getOpen = async (projectId: string): Promise<ConversationSummary | null> => {
    const response = await apiClient.get<{ conversation: ConversationSummary | null }>(
      conversationsPath(projectId),
      { params: { status: "open" } },
    );
    return response.data.conversation ?? null;
  };

  return {
    node,
    start,
    getOpen,
    fetchView: async (projectId, runId, afterSeq = 0) => {
      const response = await apiClient.get<ConversationView>(runPath(projectId, runId), {
        params: { after_seq: afterSeq },
      });
      return response.data;
    },
    openStream: (projectId, runId, afterSeq, handlers) =>
      streamConversation(`${runPath(projectId, runId)}/stream`, runId, afterSeq, handlers),
    sendMessage: async (projectId, runId, body) => {
      const response = await apiClient.post<MessageRouting>(`${runPath(projectId, runId)}/messages`, body);
      return response.data;
    },
    interrupt: async (projectId, runId) => {
      await apiClient.post(`${runPath(projectId, runId)}/interrupt`);
    },
    end: async (projectId, runId) => {
      await apiClient.post(`${runPath(projectId, runId)}/end`);
    },
    openOrResume: async (projectId, activityCode, context) => {
      const open = await getOpen(projectId);
      if (open) return { runId: open.run_id, activityCode: open.activity_code, resumed: true };
      const started = await start(projectId, activityCode, context);
      return { runId: started.run_id, activityCode, resumed: false };
    },
  };
}
